import { PlayerEvent, type PlayerInterface } from '@foundation-player/loader';
import { readonly, writable, type Readable, type Writable } from 'svelte/store';
import type { NextMediumMetadata } from '../types/player-state';
import type { PlayerStoreModule } from './player-store-types';

// We should not import from the foundation-ui. However, it would require a huge
// amount of work to separate the Foundation-UI parts from the PlayerState. So
// we keep it here.
// TODO: Separate shared and foundation-ui PlayerState.
import type { UIConfig } from '../../foundation-ui/types/ui-config';

export class MetadataPlayerStore implements PlayerStoreModule<'poster' | 'headline' | 'secondaryHeadline'> {
  readonly #poster: Writable<string | undefined> = writable();
  readonly poster: Readable<string | undefined> = readonly(this.#poster);

  readonly #headline: Writable<string | undefined> = writable();
  readonly headline: Readable<string | undefined> = readonly(this.#headline);

  readonly #secondaryHeadline: Writable<string | undefined> = writable();
  readonly secondaryHeadline: Readable<string | undefined> = readonly(this.#secondaryHeadline);

  readonly #nextMediumMetadata: Writable<NextMediumMetadata | undefined> = writable();
  readonly nextMediumMetadata: Readable<NextMediumMetadata | undefined> = readonly(this.#nextMediumMetadata);

  readonly #linkToShare: Writable<string | undefined> = writable();
  readonly linkToShare: Readable<string | undefined> = readonly(this.#linkToShare);

  #player: PlayerInterface | undefined;

  constructor(uiConfig?: UIConfig) {
    this.#poster.set(uiConfig?.poster);
    this.#headline.set(uiConfig?.headline);
    this.#secondaryHeadline.set(uiConfig?.secondaryHeadline);
  }

  initializeStateFromPlayer(player: PlayerInterface): void {
    this.#player = player;

    player.addEventListener(PlayerEvent.Ready, () => {
      this.#syncMetadataFromPlayer();
    });
    player.addEventListener(PlayerEvent.MetadataUpdated, () => {
      this.#syncMetadataFromPlayer();
    });
  }

  #syncMetadataFromPlayer() {
    const mediaConfigMetadata = this.#player?.getMediaConfig()?.metadata;

    if (mediaConfigMetadata) {
      // If any value is omitted in the metadata, we keep the old value
      // reset a value by passing an empty string
      // Thereby the integrator does not have to set the metadata twice for the
      // first medium (once in the UIConfig (to show the values until loadMedia
      // is called), once in the MediaConfig).
      if ('poster' in mediaConfigMetadata) {
        this.#poster.set(mediaConfigMetadata.poster);
      }
      if ('headline' in mediaConfigMetadata) {
        this.#headline.set(mediaConfigMetadata.headline);
      }
      if ('secondaryHeadline' in mediaConfigMetadata) {
        this.#secondaryHeadline.set(mediaConfigMetadata.secondaryHeadline);
      }
      if ('nextMedium' in mediaConfigMetadata) {
        this.#nextMediumMetadata.set(mediaConfigMetadata.nextMedium);
      }
      if ('linkToShare' in mediaConfigMetadata) {
        this.#linkToShare.set(mediaConfigMetadata.linkToShare);
      }
    }
  }
}
